import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from '@shared/services';
import { MeResponse } from '@core/models/interface';

@Injectable({
    providedIn: 'root',
})
export class RedirectService {
    private store = inject(LocalStorageService);
    private readonly REDIRECT_KEY = 'auth_redirect_url';
    private readonly blockedRedirects = [
        '/authentication/signin',
        '/authentication/token-login',
    ];
    private readonly legacyDefaultHome = '/organization/employees';
    private readonly technicalDefaultRedirects = [
        '/',
        '/dashboard',
        '/dashboard/main',
        '/admin/dashboard',
        '/admin/dashboard/main',
    ];

    setRedirectUrl(url: string) {
        this.store.set(this.REDIRECT_KEY, url);
    }

    getRedirectUrl(): string | null {
        return this.store.get(this.REDIRECT_KEY) as string | null;
    }

    clearRedirectUrl() {
        this.store.remove(this.REDIRECT_KEY);
    }

    consumeRedirectUrl(fallbackUrl: string): string {
        const redirectUrl = this.getRedirectUrl();
        this.clearRedirectUrl();
        const normalizedFallback = this.normalizeUrl(fallbackUrl);

        if (
            redirectUrl &&
            !this.isBlockedRedirect(redirectUrl) &&
            !this.shouldIgnoreRedirect(redirectUrl, normalizedFallback)
        ) {
            return this.normalizeUrl(redirectUrl);
        }

        return normalizedFallback;
    }

    getDefaultHomeUrl(profile?: MeResponse | null): string {
        const homePage = this.resolveHomePage(profile);
        if (homePage) {
            return this.normalizeUrl(homePage);
        }
        return this.legacyDefaultHome;
    }

    private isBlockedRedirect(url: string): boolean {
        return this.blockedRedirects.some((blockedUrl) => url.includes(blockedUrl));
    }

    private shouldIgnoreRedirect(redirectUrl: string, fallbackUrl: string): boolean {
        const normalizedRedirect = this.normalizeUrl(redirectUrl);

        if (
            normalizedRedirect === this.legacyDefaultHome &&
            fallbackUrl !== this.legacyDefaultHome
        ) {
            return true;
        }

        return this.technicalDefaultRedirects.includes(normalizedRedirect) &&
            fallbackUrl !== normalizedRedirect;
    }

    private normalizeUrl(url: string): string {
        const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

        if (normalizedUrl.startsWith('/admin/')) {
            return normalizedUrl.replace('/admin', '');
        }

        return normalizedUrl;
    }

    private resolveHomePage(profile?: MeResponse | null): string | null {
        const nestedUser = profile?.user as Record<string, unknown> | undefined;
        const candidates = [
            profile?.home,
            profile?.user?.home,
            nestedUser?.['home'],
            nestedUser?.['homePage'],
            nestedUser?.['homepage'],
            nestedUser?.['home_page'],
        ];

        const homePage = candidates.find(
            (value): value is string => typeof value === 'string' && value.trim().length > 0
        );

        return homePage?.trim() || null;
    }
}
